import { Injectable } from '@angular/core';
import { HttpClient } from '@angular/common/http';
import { Observable } from 'rxjs';
import { Globals } from '../global/globals';
import { Page } from '../project-corpus/domain/types';

export interface FullTextPaper {
  id: number; // paperToProjectId
  paperId: number;
  title: string;
  authors: string;
  doi: string;
  year: number;
  source: string;
  status: string; // PENDING, DOWNLOADING, MISSING, AWAITING_VERIFICATION, VERIFIED, UNAVAILABLE, FAILED
  lockedByUserName: string | null;
  pdfId: number | null;
  pdfUrl: string | null;
  verifiedByUserName: string | null;
  verifiedAt: string | null;
}

@Injectable({
  providedIn: 'root',
})
export class FullTextRetrievalService {
  constructor(
    private httpClient: HttpClient,
    private globals: Globals,
  ) {}

  private getBaseUri(projectId: string): string {
    return `${this.globals.backendUri}/projects/${projectId}/full-text-retrieval`;
  }

  getPapers(
    projectId: string,
    page?: number,
    size?: number,
    sort?: string,
    q?: string,
    status?: string,
  ): Observable<Page<FullTextPaper>> {
    let params: any = {};
    if (page !== undefined) params.page = page.toString();
    if (size !== undefined) params.size = size.toString();
    if (sort !== undefined) params.sort = sort;
    if (q !== undefined && q.trim()) params.q = q.trim();
    if (status !== undefined && status !== 'ALL') params.status = status;

    return this.httpClient.get<Page<FullTextPaper>>(`${this.getBaseUri(projectId)}/papers`, {
      params,
    });
  }

  acquireLock(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/lock`, {});
  }

  releaseLock(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/unlock`, {});
  }

  verifyPaper(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/verify`, {});
  }

  markPaperUnavailable(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/unavailable`, {});
  }

  undoDecision(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/undo`, {});
  }

  advanceToFullTextScreening(projectId: string): Observable<void> {
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/advance`, {});
  }

  retryDownload(projectId: string, paperId: number): Observable<void> {
    return this.httpClient.post<void>(
      `${this.getBaseUri(projectId)}/${paperId}/retry-download`,
      {},
    );
  }

  uploadPdf(projectId: string, paperId: number, file: File): Observable<void> {
    const formData = new FormData();
    formData.append('file', file);
    return this.httpClient.post<void>(`${this.getBaseUri(projectId)}/${paperId}/pdf`, formData);
  }
}
